import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { SIGN_UP_URL, useAuth } from '../api.ts';

import { Layout } from './Layout.tsx';

const SLOW_LOAD_MS = 4_000;

/**
 * Authenticated root. Branches on auth state but keeps the Layout
 * element type stable so React doesn't unmount/remount the subtree —
 * remounting was triggering React Query to refetch on every state
 * transition (errored queries have `dataUpdatedAt: 0` and are
 * considered stale on every fresh mount), which caused a tight
 * loading↔signed-out loop hammering /v1/users/me.
 *
 * Branches:
 *   - loading: spinner + slow-load reload nudge after SLOW_LOAD_MS
 *   - error: surface message + Try again
 *   - signed-out: redirect to the WorkOS hosted sign-up screen (no
 *                 in-app marketing surface)
 *   - signed-in + 0 orgs: redirect to /onboarding/connect-repo,
 *                         which auto-provisions the first org and
 *                         asks for the repo link (GitHub or GitLab)
 *   - signed-in + 1+ orgs: redirect to /resilience (the canonical
 *                          post-sign-in view; integrations config
 *                          lives under /settings/data-sources)
 */
export function Landing() {
  const auth = useAuth();
  const slow = useSlowLoadFlag(auth.status === 'loading');

  useEffect(() => {
    if (auth.status === 'signed-out') {
      // Full-page navigation: SIGN_UP_URL is the API origin, which 302s
      // to the WorkOS hosted sign-up page.
      window.location.replace(SIGN_UP_URL);
    }
  }, [auth.status]);

  if (auth.status === 'signed-in' && auth.me.orgs.length === 0) {
    return <Navigate to="/onboarding/connect-repo" replace />;
  }

  if (auth.status === 'signed-in') {
    return <Navigate to="/resilience" replace />;
  }

  return (
    <Layout title="Technical Accounting">
      {auth.status === 'loading' ? (
        <LoadingBody slow={slow} />
      ) : auth.status === 'error' ? (
        <ErrorBody message={auth.message} />
      ) : (
        <p className="muted">Taking you to sign-up…</p>
      )}
    </Layout>
  );
}

function LoadingBody({ slow }: { slow: boolean }) {
  return (
    <>
      <p className="muted">Loading…</p>
      {slow ? (
        <p className="muted">
          Taking longer than expected. If it doesn&apos;t resolve in a few seconds, try{' '}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.location.reload();
            }}
          >
            reloading
          </a>
          .
        </p>
      ) : null}
    </>
  );
}

function ErrorBody({ message }: { message: string }) {
  return (
    <>
      <h1 className="view-h1">Couldn&apos;t reach the API</h1>
      <p className="view-subtitle">{message}</p>
      <p>
        <a
          href="#"
          className="btn"
          onClick={(e) => {
            e.preventDefault();
            window.location.reload();
          }}
        >
          Try again
        </a>
      </p>
    </>
  );
}

function useSlowLoadFlag(loading: boolean): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!loading) {
      setSlow(false);
      return;
    }
    const t = setTimeout(() => setSlow(true), SLOW_LOAD_MS);
    return () => clearTimeout(t);
  }, [loading]);
  return slow;
}
